import { Input } from "components/Input"
import { MapMarkerAlt } from "components/Icon"
import getCountryISO3 from "country-iso-2-to-3"
import Downshift from "downshift"
import * as React from "react"
import { FloatingMenuList } from "components/Menu"
import { MenuItem } from "components/MenuItem"
import { config } from "config"
import * as PropTypes from "prop-types"
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps"
import { useAsync } from "@react-hook/async"
import { Spinner } from "components/Spinner"
import classNames from "classnames"

export function GoogleMapsAddressInput(props) {
  return (
    <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      <GoogleMapsAddressInputInner {...props} />
    </APIProvider>
  )
}

export const GoogleMapsAddressInputInner = ({
  name,
  id,
  placeholder,
  onSelectPlace,
  value,
  onChange,
  types,
}) => {
  const [suggestions, setSuggestions] = React.useState([])
  const [referenceElement, setReferenceElement] = React.useState([])

  const typesGoogle = types
    ?.map((type) => typesMapping.get(type))
    .filter(Boolean)

  const { getQueryPredictions, isActive } = useGoogleMapsAutocomplete()

  const [request, updateSuggestions] = useAsync(async (query) => {
    if (query.trim() === "") {
      setSuggestions([])
      return
    }

    try {
      const predictionsResult = await getQueryPredictions(query, typesGoogle)
      setSuggestions(predictionsResult)
    } catch (err) {
      console.error("Error while fetching autocomplete suggestions:", err)
    }
  }, 300)

  const handleInputChange = (inputValue) => {
    if (onChange) {
      onChange(inputValue)
    }

    updateSuggestions(inputValue)
  }

  const handleChange = async (selectedItem) => {
    const { place } = await selectedItem.placePrediction.toPlace().fetchFields({
      fields: ["location", "formattedAddress", "addressComponents"],
    })

    onSelectPlace(resultToPlace(place))
  }

  return (
    <Downshift
      onChange={handleChange}
      itemToString={(item) => (item ? item.placePrediction.text.text : "")}
      onInputValueChange={handleInputChange}
      inputValue={value}
      stateReducer={stateReducer}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        highlightedIndex,
        getRootProps,
      }) => (
        <div
          {...getRootProps(
            {
              className: "relative",
            },
            { suppressRefError: true },
          )}
          ref={setReferenceElement}
        >
          <Input
            type="text"
            {...getInputProps({
              name,
              id,
              placeholder,
              disabled: !isActive,
            })}
            startAdornment={
              <MapMarkerAlt className="w-4 text-primary-default" />
            }
            endAdornment={
              <div
                className={classNames({
                  invisible: isActive && request.status !== "loading",
                })}
              >
                <Spinner size={20} />
              </div>
            }
          />
          <FloatingMenuList
            {...getMenuProps()}
            referenceElement={referenceElement}
            isOpen={isOpen && suggestions.length > 0}
          >
            {suggestions.map((item, index) => (
              <MenuItem
                {...getItemProps({
                  index,
                  item,
                  focused: highlightedIndex === index,
                  label: item.placePrediction.text.text,
                })}
                key={item.placePrediction.placeId}
              />
            ))}
          </FloatingMenuList>
        </div>
      )}
    </Downshift>
  )
}

GoogleMapsAddressInput.propTypes = {
  types: PropTypes.arrayOf(PropTypes.oneOf(["city"])),
}

const typesMapping = new Map([["city", "(cities)"]])

const stateReducer = (state, changes) => {
  switch (changes.type) {
    case Downshift.stateChangeTypes.blurInput:
    case Downshift.stateChangeTypes.mouseUp:
      return {
        ...changes,
        inputValue: state.inputValue,
      }

    default:
      return changes
  }
}

const resultToPlace = (result) => {
  const country = result.addressComponents.find((component) =>
    component.types.includes("country"),
  )?.shortText

  return {
    nb:
      result.addressComponents.find((component) =>
        component.types.includes("street_number"),
      )?.longText || "",
    street:
      result.addressComponents.find((component) =>
        component.types.includes("route"),
      )?.longText || "",
    zipcode:
      result.addressComponents.find((component) =>
        component.types.includes("postal_code"),
      )?.longText || "",
    state:
      result.addressComponents.find((component) =>
        component.types.includes("administrative_area_level_2"),
      )?.longText || "",
    city:
      result.addressComponents.find((component) =>
        component.types.includes("locality"),
      )?.longText || "",
    country: country ? getCountryISO3(country) : "",
    raw: result.formattedAddress,
    formatted: result.formattedAddress,
    coords: {
      lat: result.location.lat(),
      lng: result.location.lng(),
    },
  }
}

function useGoogleMapsAutocomplete() {
  const placesLibrary = useMapsLibrary("places")

  /**
   * @param {string} input
   * @param {string[]} [types]
   */
  async function getQueryPredictions(input, types = []) {
    if (!placesLibrary) throw new Error("Places library is not active yet")

    const sessionToken = new placesLibrary.AutocompleteSessionToken()

    const request = {
      input,
      sessionToken,
      includedPrimaryTypes: types,
    }

    const { suggestions } =
      await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request,
      )

    return suggestions
  }

  return { getQueryPredictions, isActive: placesLibrary !== null }
}
